import React, { useState, useEffect, useCallback } from "react";
import { Col, Row, Card, Badge, Form, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import axios from "axios";
import EmptyState from "./EmptyState";

const PER_PAGE = 9;

const SEARCH_DEBOUNCE_MS = 500;

const LatestRansomware = ({ queryParam = "" }) => {
  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(() => {
    setLoading(true);

    let url = `${process.env.REACT_APP_EAS_BASE_URL}/api/ransomware/latest-records`;
    const params = new URLSearchParams();

    // Add pagination params
    params.append("page", page);
    params.append("per_page", PER_PAGE);

    // Add search if present
    if (search) {
      params.append("search", search);
    }

    // Combine with existing query params (remove leading ?)
    const existingParams = queryParam.startsWith("?")
      ? queryParam.slice(1)
      : queryParam;

    const finalParams = existingParams
      ? `${params.toString()}&${existingParams}`
      : params.toString();

    url = `${url}?${finalParams}`;

    axios
      .get(url, {
        headers: {
          apikey: process.env.REACT_APP_EAS_API_KEY,
          "X-CloudManager-Id": process.env.REACT_APP_EAS_CLOUD_ID,
        },
      })
      .then((response) => {
        // Handle paginated response
        if (response.data?.records) {
          setRecords(response.data.records);
          setTotal(response.data.total);
          setPages(response.data.pages);
        } else {
          // Legacy response (array)
          setRecords(response.data || []);
          setTotal(response.data?.length || 0);
          setPages(1);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch ransomware records:", error);
        setRecords([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [page, search, queryParam]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset to page 1 when search or filters change
  useEffect(() => {
    setPage(1);
  }, [search, queryParam]);

  // Debounced search - triggers after user stops typing or immediately when cleared
  useEffect(() => {
    if (searchInput === "") {
      setSearch("");
      return;
    }

    const timeoutId = setTimeout(() => {
      setSearch(searchInput);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  const handlePrevious = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNext = () => {
    if (page < pages) {
      setPage(page + 1);
    }
  };

  const hasData = records?.length > 0;
  const startItem = (page - 1) * PER_PAGE + 1;
  const endItem = Math.min(page * PER_PAGE, total);

  return (
    <Col md={12} className="mb-4">
      <Card>
        <Card.Body>
          <h6 className="fw-bold text-muted mb-3">Latest Ransomware Attacks</h6>
          <div className="position-relative mb-4">
            <Form.Control
              type="text"
              placeholder="Search company, domain, group, sector, country..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{ paddingRight: "45px" }}
            />
            <span
              className="position-absolute top-50 end-0 translate-middle-y text-muted"
              style={{ paddingRight: "12px" }}
            >
              <FontAwesomeIcon icon={faSearch} />
            </span>
          </div>

          {loading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "250px" }}
            >
              <div className="spinner-border text-muted" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : !hasData ? (
            <EmptyState title="No ransomware attacks found" />
          ) : (
            <>
              <Row>
                {records.map((r, i) => {
                  const sector = r?.sector
                    ?.match(/[A-Z][a-z]+|[0-9]+/g)
                    ?.join(" ");
                  const formattedDate = moment(r?.date).format("MMM Do YYYY");

                  return (
                    <Col key={r?.id || i} lg={4} md={6} sm={12} className="mb-4">
                      <Card className="ransomware-card h-100">
                        <div
                          className="ransomware-card-image"
                          style={
                            r?.image
                              ? { backgroundImage: `url(${r.image})` }
                              : undefined
                          }
                        >
                          {!r?.image && (
                            <div className="ransomware-card-fallback">
                              <svg
                                width="48"
                                height="48"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <rect
                                  x="3"
                                  y="3"
                                  width="18"
                                  height="18"
                                  rx="2"
                                  ry="2"
                                />
                                <circle cx="8.5" cy="8.5" r="1.5" />
                                <polyline points="21 15 16 10 5 21" />
                              </svg>
                            </div>
                          )}
                        </div>

                        <Card.Body className="p-3">
                          <div className="mb-2">
                            <Badge bg="danger" style={{ fontSize: "10px" }}>
                              {r?.group_name}
                            </Badge>{" "}
                            {sector && (
                              <Badge
                                bg="warning"
                                text="dark"
                                style={{ fontSize: "10px" }}
                              >
                                {sector}
                              </Badge>
                            )}
                          </div>

                          {r?.company && (
                            <h6 className="mb-1 text-dark">{r.company}</h6>
                          )}
                          {r?.country && (
                            <small className="d-block text-muted">
                              {r.country}
                            </small>
                          )}
                          {r?.domain && (
                            <small className="d-block text-muted">
                              {r.domain}
                            </small>
                          )}
                          <small className="d-block text-muted mt-1">
                            {formattedDate}
                          </small>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>

              {/* Pagination */}
              {total > PER_PAGE && (
                <div className="d-flex justify-content-between align-items-center border-top pt-3 mt-2">
                  <p className="small text-muted mb-0">
                    Showing <span className="fw-semibold">{startItem}</span> to{" "}
                    <span className="fw-semibold">{endItem}</span> of{" "}
                    <span className="fw-semibold">{total}</span> results
                  </p>
                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handlePrevious}
                      disabled={page <= 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleNext}
                      disabled={page >= pages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </Col>
  );
};

export default LatestRansomware;
